import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { AppShell } from '../../components/AppShell'
import { CreateObjectButton } from '@/components/CreateObjectButton'
import { AdUnitAttentionCard } from '@/components/AdUnitAttentionCard'
import { RecentObjectsCard } from '@/components/RecentObjectsCard'
import { StatusPill } from '@/components/DeliveryStatusBadge'
import { StatsPanel } from '@/components/StatsPanel'
import { AdUnitsApi, type AdUnit } from '@/services/adUnits/adUnitsApi'
import { SitesApi } from '@/services/sites/sitesApi'

/** Every ad unit in the network, split into time buckets. */
const NETWORK_STATS_QUERY = { all: 'adUnit', groupBy: 'HALF_HOURLY' } as const

export function PublishingHomePage() {
  const { t } = useTranslation()

  // A new ad unit that never gets requests usually means its tag is not on the page yet.
  const traffic: ColumnDef<AdUnit, unknown> = {
    accessorKey: 'requestedInLastDay',
    header: t('publishingHome.traffic'),
    size: 150,
    cell: ({ row }) => (
      <StatusPill tone={row.original.requestedInLastDay ? 'green' : 'gray'}>
        {row.original.requestedInLastDay ? t('publishingHome.requested') : t('publishingHome.notRequested')}
      </StatusPill>
    ),
  }

  return (
    <AppShell crumbs={[{ label: t('nav.home') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('publishingHome.title')}</h1>
            <p>{t('publishingHome.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <CreateObjectButton type="site" size="default" />
            <CreateObjectButton type="adUnit" size="default" />
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <AdUnitAttentionCard />
          <StatsPanel
            query={NETWORK_STATS_QUERY}
            storageKey="publishing-home"
            defaultRange="30d"
            variant="publishing"
          />
          <div className="grid gap-4 lg:grid-cols-2">
            <RecentObjectsCard
              title={t('publishingHome.recentAdUnits')}
              sort="createTime"
              queryKey={['adUnits']}
              fetch={AdUnitsApi.getRecent}
              href={(adUnit) => `/publishing/ad-units/${adUnit.id}`}
              sub={(adUnit) => adUnit.site?.name}
              columns={[traffic]}
            />
            <RecentObjectsCard
              title={t('publishingHome.recentSites')}
              sort="createTime"
              queryKey={['sites']}
              fetch={SitesApi.getRecent}
              href={(site) => `/publishing/sites/${site.id}`}
              sub={(site) => site.earningsAccountName}
            />
          </div>
        </div>
      </div>
    </AppShell>
  )
}
